import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { TagEditorProps } from './TagEditor.types'
import {
  EditorContainer,
  EditorHeader,
  CloseButton,
  TagsList,
  TagItem,
  TagColor,
  TagName,
  TagType,
  RemoveButton,
  NewTagSection,
  TagInput,
  ColorPicker,
  ColorOption,
  ActionButtons,
  AddButton,
  SaveButton,
  CancelButton,
  CreateNewButton,
  SearchSection,
  SearchInput,
  ExistingTagsList,
  ExistingTagItem,
  ExistingTagColor,
  ExistingTagName,
  TagTypeToggle,
  WarningMessage,
  EmptyMessage,
  LoadingSpinner,
  MobileBackdrop,
} from './TagEditor.styles'

const PRESET_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFEAA7', // Yellow
  '#DDA0DD', // Plum
  '#F4A261', // Orange
  '#E76F51', // Coral
  '#A8DADC', // Light Blue
  '#457B9D', // Dark Blue
]

export const TagEditor: React.FC<TagEditorProps> = ({
  tags,
  allTags,
  loading,
  error,
  onClose,
  onAddTag,
  onRemoveTag,
  onCreateTag,
  onLoadAllTags,
}) => {
  const { t } = useTranslation('ui')
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateNew, setShowCreateNew] = useState(false)
  const [newTagValue, setNewTagValue] = useState('')
  const [newTagColor, setNewTagColor] = useState(PRESET_COLORS[0])
  const [newTagIsPrimary, setNewTagIsPrimary] = useState(true) // Default to primary
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null)
  const [selectedTagIsPrimary, setSelectedTagIsPrimary] = useState(true) // Default to primary
  const [localError, setLocalError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Load all available tags when editor opens
    onLoadAllTags()
  }, [onLoadAllTags])

  useEffect(() => {
    // Handle click outside to close
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleClose = () => {
    onClose()
    setShowCreateNew(false)
    setNewTagValue('')
    setNewTagColor(PRESET_COLORS[0])
    setNewTagIsPrimary(true) // Reset to default primary
    setSelectedTagId(null)
    setSelectedTagIsPrimary(true) // Reset to default primary
    setLocalError(null)
  }

  const handleRemoveTag = async (tagRelationId: string) => {
    try {
      setLocalError(null)
      await onRemoveTag(tagRelationId)
    } catch (err: any) {
      setLocalError(err.message || 'Failed to remove tag')
    }
  }

  const handleAddExistingTag = async () => {
    if (!selectedTagId) return

    try {
      setLocalError(null)

      // Check if adding primary tag when one already exists
      if (selectedTagIsPrimary && tags.some((tag) => tag.primary)) {
        if (!window.confirm(t('tags.primaryConfirm'))) {
          return
        }
      }

      await onAddTag(selectedTagId, selectedTagIsPrimary)
      setSelectedTagId(null)
      setSelectedTagIsPrimary(true) // Reset to default primary
    } catch (err: any) {
      setLocalError(err.message || 'Failed to add tag')
    }
  }

  const handleCreateNewTag = async () => {
    if (!newTagValue.trim()) return

    try {
      setLocalError(null)

      // Check if adding primary tag when one already exists
      if (newTagIsPrimary && tags.some((tag) => tag.primary)) {
        if (!window.confirm(t('tags.primaryConfirm'))) {
          return
        }
      }

      await onCreateTag(newTagValue, newTagColor, newTagIsPrimary)
      setNewTagValue('')
      setNewTagColor(PRESET_COLORS[0])
      setNewTagIsPrimary(true) // Reset to default primary
      setShowCreateNew(false)
    } catch (err: any) {
      setLocalError(err.message || 'Failed to create tag')
    }
  }

  // Filter existing tags that are not already assigned
  const availableTags = allTags.filter(
    (tag) => !tags.some((tagRelation) => tagRelation.tag_id === tag.id)
  )

  // Filter available tags based on search
  const filteredTags = availableTags.filter((tag) =>
    tag.value.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Separate primary and secondary tags
  const primaryTag = tags.find((t) => t.primary)
  const secondaryTags = tags.filter((t) => !t.primary)

  const displayError = localError || error

  return (
    <>
      <MobileBackdrop onClick={handleClose} />
      <EditorContainer ref={containerRef}>
        <EditorHeader>
          <h3>{t('tags.manageTitle')}</h3>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </EditorHeader>

        {displayError && <WarningMessage>{displayError}</WarningMessage>}

        {loading && <LoadingSpinner />}

        <TagsList>
          <h4>{t('tags.currentTags')}</h4>
          {tags.length === 0 ? (
            <EmptyMessage>{t('tags.noTagsYet')}</EmptyMessage>
          ) : (
            <>
              {primaryTag && (
                <TagItem key={primaryTag.id}>
                  <TagColor $color={primaryTag.tag.color} />
                  <TagName>{primaryTag.tag.value}</TagName>
                  <TagType $isPrimary>{t('tags.primary')}</TagType>
                  <RemoveButton onClick={() => handleRemoveTag(primaryTag.id)}>
                    ×
                  </RemoveButton>
                </TagItem>
              )}
              {secondaryTags.map((tag) => (
                <TagItem key={tag.id}>
                  <TagColor $color={tag.tag.color} />
                  <TagName>{tag.tag.value}</TagName>
                  <TagType>{t('tags.secondary')}</TagType>
                  <RemoveButton onClick={() => handleRemoveTag(tag.id)}>
                    ×
                  </RemoveButton>
                </TagItem>
              ))}
            </>
          )}
        </TagsList>

        {!showCreateNew ? (
          <SearchSection>
            <h4>{t('tags.addExistingTag')}</h4>
            <SearchInput
              type="text"
              placeholder={t('tags.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <ExistingTagsList>
              {filteredTags.length === 0 ? (
                <EmptyMessage>
                  {searchQuery
                    ? t('tags.noMatchingTags')
                    : t('tags.noAvailableTags')}
                </EmptyMessage>
              ) : (
                filteredTags.map((tag) => (
                  <ExistingTagItem
                    key={tag.id}
                    $selected={selectedTagId === tag.id}
                    onClick={() => {
                      setSelectedTagId(tag.id)
                      setSelectedTagIsPrimary(true) // Reset to primary when selecting a new tag
                    }}
                  >
                    <ExistingTagColor $color={tag.color} />
                    <ExistingTagName>{tag.value}</ExistingTagName>
                  </ExistingTagItem>
                ))
              )}
            </ExistingTagsList>

            {selectedTagId && (
              <>
                <TagTypeToggle>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedTagIsPrimary}
                      onChange={(e) =>
                        setSelectedTagIsPrimary(e.target.checked)
                      }
                    />
                    {t('tags.setPrimary')}
                  </label>
                </TagTypeToggle>
                {selectedTagIsPrimary && tags.some((tag) => tag.primary) && (
                  <WarningMessage>{t('tags.primaryWarning')}</WarningMessage>
                )}
              </>
            )}

            <ActionButtons>
              <CreateNewButton onClick={() => setShowCreateNew(true)}>
                {t('tags.createNewTag')}
              </CreateNewButton>
              {selectedTagId && (
                <AddButton onClick={handleAddExistingTag}>
                  {t('tags.addSelectedTag')}
                </AddButton>
              )}
            </ActionButtons>
          </SearchSection>
        ) : (
          <NewTagSection>
            <h4>{t('tags.createNewTag')}</h4>
            <TagInput
              type="text"
              placeholder={t('tags.tagNamePlaceholder')}
              value={newTagValue}
              onChange={(e) => setNewTagValue(e.target.value)}
              maxLength={50}
            />

            <ColorPicker>
              {PRESET_COLORS.map((color) => (
                <ColorOption
                  key={color}
                  $color={color}
                  $selected={newTagColor === color}
                  onClick={() => setNewTagColor(color)}
                />
              ))}
            </ColorPicker>

            <TagTypeToggle>
              <label>
                <input
                  type="checkbox"
                  checked={newTagIsPrimary}
                  onChange={(e) => setNewTagIsPrimary(e.target.checked)}
                />
                {t('tags.setPrimary')}
              </label>
            </TagTypeToggle>

            {newTagIsPrimary && tags.some((tag) => tag.primary) && (
              <WarningMessage>{t('tags.primaryWarning')}</WarningMessage>
            )}

            <ActionButtons>
              <CancelButton
                onClick={() => {
                  setShowCreateNew(false)
                  setNewTagValue('')
                  setNewTagColor(PRESET_COLORS[0])
                  setNewTagIsPrimary(true) // Reset to default primary
                }}
              >
                {t('tags.cancel')}
              </CancelButton>
              <SaveButton
                onClick={handleCreateNewTag}
                disabled={!newTagValue.trim()}
              >
                {t('tags.createAndAdd')}
              </SaveButton>
            </ActionButtons>
          </NewTagSection>
        )}
      </EditorContainer>
    </>
  )
}
